<template>
  <div v-if="showTitle" class="page-title">
    <div class="row">
      <div class="col-sm-6 col-12">
        <!-- Dynamically update title based on mode -->
        <h2 v-if="isNew">{{ `Create ${formName}` }}</h2>
        <h2 v-else-if="isEditMode">{{ `Edit ${formName}` }}</h2>
        <h2 v-else>{{ `View ${formName}` }}</h2>
      </div>
    </div>
  </div>

  <div class="col-sm-12">
    <div class="card">
      <div class="card-body">
        <form @submit.prevent="handleSubmit">
          <slot></slot>
        </form>
      </div>

      <div class="card-footer pt-0">
        <div class="pull-right">
          <!-- Display different buttons based on mode -->
          <button class="btn btn-primary me-3" v-if="isEditMode" type="submit" @click="handleSubmit">
            {{ isNew ? 'Create' : 'Update' }}
          </button>
          <button class="btn btn-danger" v-if="isEditMode" type="button" @click="cancelEdit">
            Cancel
          </button>
          <button class="btn btn-primary" v-if="isReadOnly && !isNew && !lockEditing" @click.prevent="enableEditMode">
            Edit
          </button>
          <span v-if="isReadOnly && !isNew && lockEditing" class="text-muted fst-italic">
            This record cannot be edited.
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {ref, onBeforeMount} from 'vue';
import {Model} from '../modelTemplates/modelTemplates';
import {notificationType, showToast} from "../utility/toastNotificationUtility";
import {newEntityId} from '../globals.js';

// Props
const props = defineProps({
  entityId: {type: String, required: true}, //the id of the entity
  apiUrl: {type: String, required: true}, // the api for performing CRUD ops
  entity: {type: Object, required: true, default: null}, //the entity's object
  modelConstructor: {type: String, required: true}, //the resolver name for constructing the entity Object
  formName: {type: String, required: true}, //the name that the entity would be called/form title
  editState: {type: Boolean, default: false}, //The edit state being tracked by the parent
  startInEdit: {type: Boolean, default: false, required: false}, //should the form open in editMode ?
  showTitle: {type: Boolean, default: false, required: false}, // in some cases the parent will show the title, configure
  redirectUrl: {type: String, required: false}, //post save should we want to redirect from the page
  onCancelCallback: {type: Function, default: null}, //optional callback for when cancel is clicked
  preSave: {type: Function, default: null}, //optional callback prior to saving entity
  postSave: {type: Function, default: null}, //optional callback post save entity
  fileUploadMap: {type: Object, default: () => ({}), required: false}, //map of file keys to file parts
  lockEditing: {type: Boolean, default: false, required: false}, //when true, hides the Edit button for an existing (non-new) entity
});

// Emitters
const emit = defineEmits(['update:entity', 'update:editState', 'update:entityId']);

// Reactive state
const isEditMode = ref(false);
const isReadOnly = ref(false);
const internalEntityId = ref(props.entityId);
const isNew = ref(props.entityId === newEntityId);
const originalState = ref({});


// Methods
const fetchEntity = async () => {
  try {
    const response = await fetch(`${props.apiUrl}/${internalEntityId.value}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch ${props.formName}: ${response.statusText}`);
    }
    const data = await response.json();
    Object.keys(data).forEach((key) => {
      if (key in props.entity.state) {
        props.entity.state[key] = data[key];
      }
    });
    originalState.value = JSON.parse(JSON.stringify(props.entity.state));
  } catch (error) {
    showToast({
      title: 'Error',
      body: `Encountered an error loading ${props.formName}.`,
      type: notificationType.ERROR,
      delay: 7000
    });
    console.error(`Failed to fetch ${props.formName}`, error);
  }
};

const handleSubmit = async () => {
  try {
    if (props.preSave) props.preSave();
    const payload = {...props.entity.state};

    if (props.entity.isValid()) {
      let response;
      const url = isNew.value
          ? `${props.apiUrl}`
          : `${props.apiUrl}/${internalEntityId.value}`;

      // Check if we have files to upload
      if (props.fileUploadMap && Object.keys(props.fileUploadMap).length > 0) {
        // Use FormData for file uploads
        const formData = new FormData();

        // Add the entity data as a JSON string in a field called 'data'
        formData.append('data', JSON.stringify(payload));

        // Add each file to the form data
        for (const [key, file] of Object.entries(props.fileUploadMap)) {
          formData.append(key, file);
        }

        // Send the request with FormData
        const requestOptions = {
          method: isNew.value ? 'POST' : 'PUT',
          body: formData,
        };

        response = await fetch(url, requestOptions);
      } else {
        // No files, use JSON
        const requestOptions = {
          method: isNew.value ? 'POST' : 'PUT',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify(payload),
        };

        response = await fetch(url, requestOptions);
      }

      if (!response.ok) {
        let message = '';
        if (response.text) {
          message = await response.text();
        } else {
          message = `Encountered an error saving ${props.formName}. Please try again.`;
        }
        showToast({
          title: 'Submission Error',
          body: message,
          type: 'error',
          delay: 5000
        });

        return;
      }
      const responseData = await response.json();

      isReadOnly.value = true;
      isEditMode.value = false;
      emit("update:editState", false);

      //notify
      showToast({
        title: 'Success',
        body: `Successfully saved ${props.formName}.`,
        type: 'success'
      });

      await fetchEntity(); // Refresh the entity data
      if (isNew.value) {
        internalEntityId.value = responseData.id;
        emit('update:entityId', internalEntityId.value);
        if (props.postSave) props.postSave(responseData);

        if (props.redirectUrl) {
          window.location.href = `${props.redirectUrl}/${internalEntityId.value}`;
        }
      } else if (props.postSave) {
        props.postSave(responseData);
      }
    }
  } catch (error) {
    showToast({
      title: 'Oops..',
      body: `Encountered an internal error. Please contact support.`,
      type: 'error',
      delay: 7000
    });
    console.error(`Failed to submit ${props.formName}`, error);
  }
};

const enableEditMode = () => {
  isEditMode.value = true;
  isReadOnly.value = false;

  emit("update:editState", true);
};

const cancelEdit = () => {
  props.entity.state = JSON.parse(JSON.stringify(originalState.value));
  isReadOnly.value = true;
  isEditMode.value = false;
  emit("update:editState", false);
  if (props.onCancelCallback) props.onCancelCallback();
};

// Initialization
onBeforeMount(async () => {
  emit('update:entity', Model(props.modelConstructor).call({}));
  if (!isNew.value) {
    await fetchEntity();
    isReadOnly.value = true;
  } else {
    isEditMode.value = true;
  }
  if (props.startInEdit) {
    isEditMode.value = props.startInEdit;
    isReadOnly.value = !props.startInEdit;
  }

  emit("update:editState", isEditMode.value);
  if (!isNew.value) {
    originalState.value = JSON.parse(JSON.stringify(props.entity.state));
  }
});
</script>
